import {
  BooleanInput,
  coerceBooleanProperty,
  coerceCssPixelValue,
  NumberInput,
} from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatDrawer, MatDrawerMode } from '@angular/material/sidenav';
import { map, merge, Observable, startWith, Subject, takeUntil } from 'rxjs';

import { IntersectionObserverDirective } from '../../directives';

export type StickyDrawerMode = MatDrawerMode | 'cover';

@Component({
    selector: 'lui-sticky-drawer',
    templateUrl: './sticky-drawer.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class StickyDrawerComponent implements OnInit, OnDestroy {
  private _model = 'sticky-drawer';

  _topVisible = false;
  _bottomVisible = false;
  _contentVisible = false;
  _matDrawerMode: MatDrawerMode = 'side';

  @Input()
  showCloseButton = true;

  @Input()
  get opened(): boolean {
    return this.matDrawer.opened;
  }
  set opened(value: BooleanInput) {
    this.matDrawer.toggle(coerceBooleanProperty(value));
  }

  @HostBinding('class.sticky-drawer-init') initClass = false;

  @HostBinding('class') classList = {
    [`${this._model}`]: true,
  };

  @HostBinding('class.sidebar-open') get _sideBarOpenClass(): boolean {
    return this.opened;
  }

  @HostBinding('class.sidebar-closed') get _sideBarClosedClass(): boolean {
    return !this.opened;
  }

  @HostBinding('class.sidebar-side') get _sideBarSideClass(): boolean {
    return this.mode === 'side';
  }

  @HostBinding('class.sidebar-over') get _sideBarOverClass(): boolean {
    return this.mode === 'over';
  }

  @HostBinding('class.sidebar-cover') get _sideBarCoverClass(): boolean {
    return this.mode === 'cover';
  }

  @Output() openedChange = new EventEmitter<boolean>();
  @Output() modeChanged = new EventEmitter<StickyDrawerMode>();

  @ViewChild('topObserver', {
    static: true,
    read: IntersectionObserverDirective,
  })
  topObserver!: IntersectionObserverDirective;

  @ViewChild('bottomObserver', {
    static: true,
    read: IntersectionObserverDirective,
  })
  bottomObserver!: IntersectionObserverDirective;

  @ViewChild('contentObserver', {
    static: true,
    read: IntersectionObserverDirective,
  })
  contentObserver!: IntersectionObserverDirective;

  @ViewChild(MatDrawer, {
    static: true,
  })
  matDrawer!: MatDrawer;

  @Input()
  get mode(): StickyDrawerMode {
    return this._mode;
  }
  set mode(value: StickyDrawerMode) {
    this._matDrawerMode = value === 'cover' ? 'over' : value;
    this._mode = value;
    this.cd.markForCheck();
  }
  _mode: StickyDrawerMode = 'side';

  @Input() position: 'start' | 'end' = 'end';

  @Input() get drawerWidth(): string {
    return this._drawerWidth;
  }
  set drawerWidth(value: NumberInput) {
    if (!value) {
      this._drawerWidth = '0';
      return;
    }
    this._drawerWidth = typeof value === 'string' ? value : `${value}px`;
  }

  private _topGap = 0;

  @HostBinding('style.--top-gap') get topGapCssVariable() {
    return coerceCssPixelValue(this._topGap);
  }

  @Input() get topGapElement(): HTMLElement {
    return this._topGapElement;
  }
  set topGapElement(el: HTMLElement | undefined) {
    if (el) {
      this._topResizeObserver = new ResizeObserver((entries) =>
        entries.forEach((entry) => {
          const height = entry.borderBoxSize[0].blockSize;
          if (height !== this._topGap) {
            this.gapChange.emit(height);
          }
        })
      );
      this._topResizeObserver.observe(el);
    }
  }
  private _topGapElement!: HTMLElement;

  private _topResizeObserver!: ResizeObserver;

  topIntersectionMarginTop = '0px';

  _drawerWidth = '280px';

  destroy$ = new Subject<void>();

  gapChange = new EventEmitter<number>();

  constructor(private cd: ChangeDetectorRef) {}

  ngOnInit(): void {
    // Observe intersections of top, content and bottom to set appropriate positioning of navbar
    this.topObserver.intersectionChange
      .pipe(takeUntil(this.destroy$))
      .subscribe((v) => {
        this._topVisible = v;
        this._setPositionClass();
        this.cd.markForCheck();
      });
    this.bottomObserver.intersectionChange
      .pipe(takeUntil(this.destroy$))
      .subscribe((v) => {
        this._bottomVisible = v;
        this._setPositionClass();
        this.cd.markForCheck();
      });
    this.contentObserver.intersectionChange
      .pipe(takeUntil(this.destroy$))
      .subscribe((v) => {
        this._contentVisible = v;
        this._setPositionClass();
        this.cd.markForCheck();
      });

    this.gapChange.pipe(takeUntil(this.destroy$)).subscribe((height) => {
      this._topGap = height;
      this.topIntersectionMarginTop = coerceCssPixelValue(-height);
    });

    // Observe matdrawer opened events and emit upwards
    this._getIsSideBarToggleStartStream()
      .pipe(takeUntil(this.destroy$))
      .subscribe((isOpen) => this.onMatDrawerToggle(isOpen));

    setTimeout(() => {
      this.initClass = true;
    }, 10);
  }

  ngOnDestroy(): void {
    if (this._topResizeObserver) {
      this._topResizeObserver.disconnect();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggle(opened: boolean = !this.opened): void {
    this.matDrawer.toggle(opened);
    this.cd.markForCheck();
  }

  onMatDrawerToggle(isOpen: boolean): void {
    this.opened = isOpen;
  }

  private _setPositionClass() {
    const relative = this._topVisible && this._bottomVisible;
    this.classList = {
      [`${this._model}`]: true,
      relative,
      fixed: !this._topVisible && !this._bottomVisible && this._contentVisible,
      absolute: !relative && this._bottomVisible,
    };
  }

  /**
   * This method provides a merged stream of the MatDrawer openStart and closeStart events
   * track when opening and closing animations are triggered
   */
  private _getIsSideBarToggleStartStream(): Observable<boolean> {
    return merge(
      this.matDrawer.openedStart.pipe(map(() => true)),
      this.matDrawer.closedStart.pipe(map(() => false))
    ).pipe(startWith(this.matDrawer.opened));
  }
}
